import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  isSameYear,
  getQuarter,
  startOfQuarter,
  endOfQuarter,
  getWeek,
  addDays,
  parseISO,
  isWithinInterval,
  isSameDay,
  isAfter,
  isBefore,
  subHours,
  subMonths,
  setWeek,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import { FileText, Calendar, Clock, Sun, Save, X, Search, MessageSquare, Trophy, ArrowUpRight, ArrowDownRight, TrendingUp, BarChart3, ChevronDown, ChevronLeft, ChevronRight, Calculator, Coins, } from 'lucide-react';
import Skeleton from './Skeleton';
import { useTooltip } from '../lib/useTooltip';
import { logActivity } from '../lib/activity';
import { useAccentColor } from '../lib/useAccentColor';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import RevenueForm from './RevenueForm';

interface WeekData {
  dayName: string;
  dayShortName: string;
  dayNumber: number;
  totalRevenue: number;
  count: number;
  average: number;
  maxRevenue: number;
  dailyRevenues: number[];
}

interface RevenueEntry {
  date: string;
  cash_revenue: number;
  card_revenue: number;
  invoice_revenue: number;
}

interface Settings {
  weekly_revenue_goal: number;
  show_revenue_goal: boolean;
  show_tips: boolean;
}

export default function TotalOverview() {
  const { accentColor } = useAccentColor();
  const { tooltip } = useTooltip();
  const [data, setData] = useState<RevenueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [yearTotal, setYearTotal] = useState(0);
  const [weeklyAverage, setWeeklyAverage] = useState(0);
  const [currentMonthTotal, setCurrentMonthTotal] = useState(0);
  const [currentWeekTotal, setCurrentWeekTotal] = useState(0);
  const [previousMonthTotal, setPreviousMonthTotal] = useState(0);
  const [previousYearTotal, setPreviousYearTotal] = useState(0);
  const [showRevenueForm, setShowRevenueForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [weeklyGoal, setWeeklyGoal] = useState(1000);
  const [activeYears, setActiveYears] = useState<number[]>([]);
  const [showRevenueGoal, setShowRevenueGoal] = useState(true);
  const [showTips, setShowTips] = useState(false);
  const [chartAnimated, setChartAnimated] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [currentWeekHiddenTotal, setCurrentWeekHiddenTotal] = useState(0);
  const [currentMonthHiddenTotal, setCurrentMonthHiddenTotal] = useState(0);
  const [yearHiddenTotal, setYearHiddenTotal] = useState(0);
  const [hiddenWeeklyData, setHiddenWeeklyData] = useState<any[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [quarterTotal, setQuarterTotal] = useState(0);
  const [quarterHiddenTotal, setQuarterHiddenTotal] = useState(0);
  const queryClient = useQueryClient();

  // Handle pull-to-refresh
  useEffect(() => {
    let touchStart = 0;
    let touchEnd = 0;
    let scrollTop = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStart = e.touches[0].clientY;
      scrollTop = document.documentElement.scrollTop;
    };

    const handleTouchMove = (e: TouchEvent) => {
      touchEnd = e.touches[0].clientY;
      
      // Only allow pull-to-refresh when at top of page
      if (scrollTop <= 0 && touchEnd > touchStart && !refreshing) {
        const distance = touchEnd - touchStart;
        if (distance > 100) {
          setRefreshing(true);
          fetchData().then(() => {
            setRefreshing(false);
          });
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [refreshing]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: settings, error } = await supabase
          .from('settings')
          .select('weekly_revenue_goal, active_years, show_revenue_goal, show_tips')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) throw error;
        if (settings) {
          setWeeklyGoal(settings.weekly_revenue_goal);
          setActiveYears(settings.active_years || []);
          setShowRevenueGoal(settings.show_revenue_goal ?? true);
          setShowTips(settings.show_tips ?? true);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    fetchSettings();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const now = new Date();
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      setLoading(false);
      return;
    }

    const currentDate = new Date();
    const isCurrentYear = selectedYear === now.getFullYear();
    
    // Fetch hidden revenue data for the selected year
    let hiddenYearTotal = 0;
    let hiddenMonthTotal = 0;
    let hiddenWeekTotal = 0;
    let hiddenQuarterTotal = 0;
    
    if (isCurrentYear) {
      // Fetch all hidden revenue for the year
      const { data: hiddenYearData } = await supabase
        .from('hidden_revenue')
        .select('amount, date')
        .gte('date', format(startOfYear(new Date(selectedYear, 0)), 'yyyy-MM-dd'))
        .lte('date', format(endOfYear(new Date(selectedYear, 11)), 'yyyy-MM-dd'));
      
      if (hiddenYearData) {
        // Calculate year total
        hiddenYearTotal = hiddenYearData.reduce((sum, entry) => sum + entry.amount, 0);
        
        // Calculate month total
        const currentMonth = format(now, 'yyyy-MM');
        hiddenMonthTotal = hiddenYearData
          .filter(entry => format(parseISO(entry.date), 'yyyy-MM') === currentMonth)
          .reduce((sum, entry) => sum + entry.amount, 0);
        
        // Calculate week total with 7 AM cutoff
        const adjustedDateForWeek = now.getHours() < 7
          ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
          : now;
        const weekStart = startOfWeek(adjustedDateForWeek, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(adjustedDateForWeek, { weekStartsOn: 1 });
        hiddenWeekTotal = hiddenYearData
          .filter(entry => {
            const entryDate = parseISO(entry.date);
            return entryDate >= weekStart && entryDate <= weekEnd;
          })
          .reduce((sum, entry) => sum + entry.amount, 0);

        // Calculate quarter total
        const quarterStart = startOfQuarter(now);
        const quarterEnd = endOfQuarter(now);
        hiddenQuarterTotal = hiddenYearData
          .filter(entry => {
            const entryDate = parseISO(entry.date);
            return entryDate >= quarterStart && entryDate <= quarterEnd;
          })
          .reduce((sum, entry) => sum + entry.amount, 0);
      }
      
      setYearHiddenTotal(hiddenYearTotal);
      setCurrentMonthHiddenTotal(hiddenMonthTotal);
      setCurrentWeekHiddenTotal(hiddenWeekTotal);
      setQuarterHiddenTotal(hiddenQuarterTotal);
      
      // Generate weekly data for hidden revenue
      if (hiddenYearData && hiddenYearData.length > 0) {
        const hiddenWeeklyData = generateHiddenWeeklyData(selectedYear, hiddenYearData);
        setHiddenWeeklyData(hiddenWeeklyData);
      }
    }
    
    try {
      // Create date range for each active year
      const dateRanges = activeYears.map(year => ({
        start: format(startOfYear(new Date(year, 0)), 'yyyy-MM-dd'),
        end: format(endOfYear(new Date(year, 11)), 'yyyy-MM-dd')
      }));

      // Fetch data for all active years
      const promises = dateRanges.map(range =>
        supabase
          .from('revenue_entries')
          .select('*')
          .gte('date', range.start)
          .lte('date', range.end)
          .eq('user_id', user.id)
      );

      const results = await Promise.all(promises);
      
      // Combine all results and check for errors
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        throw errors[0].error;
      }

      const allEntries = results
        .map(result => result.data || [])
        .flat()
        .sort((a, b) => a.date.localeCompare(b.date));

      setData(allEntries);

      // Filter entries for the selected year
      const selectedYearEntries = allEntries.filter(entry => {
        const entryDate = parseISO(entry.date);
        return entryDate.getFullYear() === selectedYear;
      });

      const yearStart = startOfYear(new Date(selectedYear, 0));
      const yearEnd = endOfYear(new Date(selectedYear, 11));
      const lastYearStart = startOfYear(new Date(selectedYear - 1, 0));
      const lastYearEnd = new Date(selectedYear - 1, currentDate.getMonth(), currentDate.getDate());

      // For current year, use current week and month
      // For past years, use the last week and month of that year
      let weekStart, weekEnd, monthStart, monthEnd, previousMonthDate;
      
      if (isCurrentYear) {
        // Use adjusted date for week calculation to match revenue entry logic
        const adjustedDate = now.getHours() < 7
          ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
          : now;
        weekStart = startOfWeek(adjustedDate, { weekStartsOn: 1 });
        weekEnd = endOfWeek(adjustedDate, { weekStartsOn: 1 });
        monthStart = startOfMonth(now);
        monthEnd = endOfMonth(now);
        
        // Get the same day in previous month
        previousMonthDate = subMonths(now, 1);
      } else {
        // For past years, use the last month and week of the year
        const lastMonth = new Date(selectedYear, 11, 31); // December 31st
        monthStart = startOfMonth(lastMonth);
        monthEnd = endOfMonth(lastMonth);
        
        // Last week of the year
        const lastWeek = setWeek(new Date(selectedYear, 11, 31), 52, { weekStartsOn: 1 });
        weekStart = startOfWeek(lastWeek, { weekStartsOn: 1 });
        weekEnd = endOfWeek(lastWeek, { weekStartsOn: 1 });
        
        // Previous month would be November
        previousMonthDate = new Date(selectedYear, 10, 31);
      }

      if (selectedYearEntries.length > 0) {
        // Calculate selected year total up to current date for current year
        // or full year total for past years
        const yearEndDate = isCurrentYear ? now : yearEnd;
        const thisYearEntries = selectedYearEntries.filter(entry => {
          const entryDate = parseISO(entry.date);
          return entryDate.getFullYear() === selectedYear && 
                 (isCurrentYear ? 
                   isBefore(entryDate, now) || isSameDay(entryDate, now) 
                   : true
                 );
        });

        const thisYearTotal = thisYearEntries.reduce(
          (sum, entry) => sum + entry.cash_revenue + entry.card_revenue + entry.invoice_revenue,
          0
        );

        // Calculate previous year total for the same period
        const previousYearEntries = allEntries.filter(entry => {
          const entryDate = parseISO(entry.date);
          return entryDate.getFullYear() === selectedYear - 1 && 
                 (isBefore(entryDate, lastYearEnd) || isSameDay(entryDate, lastYearEnd));
        });

        const lastYearTotal = previousYearEntries.reduce(
          (sum, entry) => sum + entry.cash_revenue + entry.card_revenue + entry.invoice_revenue,
          0
        );

        // Calculate current month total
        const currentMonthEntries = selectedYearEntries.filter(entry => {
          const entryDate = parseISO(entry.date);
          return entryDate >= monthStart && entryDate <= (isCurrentYear ? now : monthEnd);
        });

        const thisMonthTotal = currentMonthEntries.reduce(
          (sum, entry) => sum + entry.cash_revenue + entry.card_revenue + entry.invoice_revenue,
          0
        );

        // Calculate previous month total for comparison
        const previousMonthStart = startOfMonth(previousMonthDate);
        const previousMonthEnd = isCurrentYear ? 
          new Date(previousMonthDate.getFullYear(), previousMonthDate.getMonth(), now.getDate()) : 
          endOfMonth(previousMonthDate);
        
        const previousMonthEntries = allEntries.filter(entry => {
          const entryDate = parseISO(entry.date);
          return entryDate >= previousMonthStart && entryDate <= previousMonthEnd;
        });

        const lastMonthTotal = previousMonthEntries.reduce(
          (sum, entry) => sum + entry.cash_revenue + entry.card_revenue + entry.invoice_revenue,
          0
        ); 

        // Calculate current week total
        const currentWeekEntries = selectedYearEntries.filter(entry => {
          const entryDate = parseISO(entry.date);
          return entryDate >= weekStart && entryDate <= weekEnd;
        });

        const thisWeekTotal = currentWeekEntries.reduce(
          (sum, entry) => sum + entry.cash_revenue + entry.card_revenue + entry.invoice_revenue,
          0
        );

        // Calculate weekly average based on completed weeks only
        const completedWeeks: Record<string, WeekData> = {};
        selectedYearEntries.forEach(entry => {
          const entryDate = parseISO(entry.date);
          // Use adjusted date for completed weeks calculation
          const adjustedNow = now.getHours() < 7
            ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
            : now;
          if (isCurrentYear ? isBefore(entryDate, startOfWeek(adjustedNow, { weekStartsOn: 1 })) : true) {
            const weekKey = `${entryDate.getFullYear()}-${getWeek(entryDate, { weekStartsOn: 1 })}`;
            if (!completedWeeks[weekKey]) {
              completedWeeks[weekKey] = { total: 0, count: 0 };
            }
            completedWeeks[weekKey].total += entry.cash_revenue + entry.card_revenue + entry.invoice_revenue;
            completedWeeks[weekKey].count++;
          }
        });

        const completedWeeksCount = Object.keys(completedWeeks).length || 1;
        const totalRevenue = Object.values(completedWeeks).reduce((sum, week) => sum + week.total, 0);
        const calculatedWeeklyAverage = totalRevenue / completedWeeksCount;

        // Calculate quarter total
        const quarterStart = startOfQuarter(now);
        const quarterEnd = endOfQuarter(now);
        const quarterEntries = selectedYearEntries.filter(entry => {
          const entryDate = parseISO(entry.date);
          return entryDate >= quarterStart && entryDate <= quarterEnd;
        });

        const thisQuarterTotal = quarterEntries.reduce(
          (sum, entry) => sum + entry.cash_revenue + entry.card_revenue + entry.invoice_revenue,
          0
        );

        setYearTotal(thisYearTotal);
        setPreviousYearTotal(lastYearTotal);
        setWeeklyAverage(calculatedWeeklyAverage);
        setCurrentMonthTotal(thisMonthTotal);
        setPreviousMonthTotal(lastMonthTotal);
        setCurrentWeekTotal(thisWeekTotal);
        setQuarterTotal(thisQuarterTotal);
      } else {
        // Reset values if no data for selected year
        setYearTotal(0);
        setPreviousYearTotal(0);
        setWeeklyAverage(0);
        setCurrentMonthTotal(0);
        setPreviousMonthTotal(0);
        setCurrentWeekTotal(0);
        setQuarterTotal(0);
      }
    } catch (error) {
      console.error('Error in fetchData:', error);
    } finally {
      setLoading(false);
      // Trigger chart animation after data is loaded
      setTimeout(() => setChartAnimated(true), 300);
    }
  };

  useEffect(() => {
    if (activeYears.length > 0) {
      fetchData();
    }
  }, [selectedYear, activeYears]);

  useEffect(() => {
    // Check if we should open a revenue form (from mobile navigation)
    const formDateToOpen = sessionStorage.getItem('openRevenueForm');
    if (formDateToOpen) {
      setSelectedDate(formDateToOpen);
      setShowRevenueForm(true);
      sessionStorage.removeItem('openRevenueForm');
    }
  }, []);

  const generateWeeklyData = (year: number) => {
    if (!data.length) return [];

    // Get current date
    const now = new Date();
    const currentYear = now.getFullYear();
    const isCurrentYear = year === currentYear;

    // Set start date to week 38 of 2024 or week 1 of the selected year if later
    const startYear = 2024;
    const startWeek = 38;
    let startDate;
    
    if (year === 2024) {
      startDate = new Date(2024, 8, 16); // September 16, 2024 (start of week 38)
    } else {
      startDate = startOfYear(new Date(year, 0, 1));
    }

    const weeks = [];
    const yearEnd = isCurrentYear ? now : endOfYear(new Date(year, 11, 31));
    let currentWeek = startOfWeek(startDate, { weekStartsOn: 1 });

    while (currentWeek <= yearEnd) {
      const weekNumber = getWeek(currentWeek, { weekStartsOn: 1, firstWeekContainsDate: 4 });
      const weekYear = currentWeek.getFullYear();
      
      // Skip weeks before week 38 of 2024 or if not in the selected year
      if ((weekYear === 2024 && weekNumber < startWeek) || weekYear !== year) {
        currentWeek = addDays(currentWeek, 7);
        continue;
      }
      
      const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
      const weekEntries = data.filter(entry => {
        const entryDate = parseISO(entry.date);
        return entryDate >= currentWeek && entryDate <= weekEnd && new Date(entryDate).getFullYear() === year;
      });

      const totals = weekEntries.reduce(
        (acc, entry) => {
          acc.total += entry.cash_revenue + entry.card_revenue + entry.invoice_revenue;
          acc.cash += entry.cash_revenue;
          acc.card += entry.card_revenue;
          acc.invoice += entry.invoice_revenue;
          return acc;
        },
        { total: 0, cash: 0, card: 0, invoice: 0 }
      );

      weeks.push({
        name: `${weekNumber}`,
        totaal: totals.total,
        contant: totals.cash,
        pin: totals.card,
        rekening: totals.invoice,
        year: year
      });

      currentWeek = addDays(currentWeek, 7);
    }

    return weeks;
  };

  const generateHiddenWeeklyData = (year: number, hiddenData: any[]) => {
    if (!hiddenData.length) return [];

    // Get current date
    const now = new Date();
    const currentYear = now.getFullYear();
    const isCurrentYear = year === currentYear;

    // Set start date to week 38 of 2024 or week 1 of the selected year if later
    const startYear = 2024;
    const startWeek = 38;
    let startDate;
    
    if (year === 2024) {
      startDate = new Date(2024, 8, 16); // September 16, 2024 (start of week 38)
    } else {
      startDate = startOfYear(new Date(year, 0, 1));
    }

    const weeks = [];
    const yearEnd = isCurrentYear ? now : endOfYear(new Date(year, 11, 31));
    let currentWeek = startOfWeek(startDate, { weekStartsOn: 1 });

    while (currentWeek <= yearEnd) {
      const weekNumber = getWeek(currentWeek, { weekStartsOn: 1, firstWeekContainsDate: 4 });
      const weekYear = currentWeek.getFullYear();
      
      // Skip weeks before week 38 of 2024 or if not in the selected year
      if ((weekYear === 2024 && weekNumber < startWeek) || weekYear !== year) {
        currentWeek = addDays(currentWeek, 7);
        continue;
      }
      
      const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
      const weekEntries = hiddenData.filter(entry => {
        const entryDate = parseISO(entry.date);
        return entryDate >= currentWeek && entryDate <= weekEnd && new Date(entryDate).getFullYear() === year;
      });

      const total = weekEntries.reduce((sum, entry) => sum + entry.amount, 0);

      weeks.push({
        name: `${weekNumber}`,
        fooi: total,
        year: year
      });

      currentWeek = addDays(currentWeek, 7);
    }

    return weeks;
  };

  const weeklyData = React.useMemo(() => {
    const baseData = generateWeeklyData(selectedYear);

    // If showTips is enabled and we have hidden weekly data, merge them
    if (showTips && hiddenWeeklyData.length > 0) {
      return baseData.map(week => {
        const hiddenWeek = hiddenWeeklyData.find(hw => hw.name === week.name);
        return {
          ...week,
          fooi: hiddenWeek ? hiddenWeek.totaal : 0
        };
      });
    }

    return baseData;
  }, [data, selectedYear, showTips, hiddenWeeklyData]);

  const currentDate = new Date();
  const isCurrentYear = selectedYear === currentDate.getFullYear();
  
  // Use the same 7 AM cutoff logic as revenue entry for week calculation
  const adjustedDateForWeek = currentDate.getHours() < 7
    ? new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 1)
    : currentDate;
  const currentWeekNumber = getWeek(adjustedDateForWeek, { weekStartsOn: 1 });

  // Calculate totals with or without hidden revenue (tips)
  const totalWeekRevenue = showTips ? currentWeekTotal + currentWeekHiddenTotal : currentWeekTotal;
  const totalMonthRevenue = showTips ? currentMonthTotal + currentMonthHiddenTotal : currentMonthTotal;
  const totalYearRevenue = showTips ? yearTotal + yearHiddenTotal : yearTotal;
  const totalWeeklyAverage = showTips ? weeklyAverage + (yearHiddenTotal / 52) : weeklyAverage;
  const totalQuarterRevenue = showTips ? quarterTotal + quarterHiddenTotal : quarterTotal;

  const weekProgress = (totalWeekRevenue / weeklyGoal) * 100;
  const progressColor = weekProgress >= 100 ? 'bg-green-500' : (showTips ? 'bg-red-500' : 'bg-accent');

  const monthPercentageChange = previousMonthTotal !== 0 
    ? ((currentMonthTotal - previousMonthTotal) / previousMonthTotal) * 100 
    : 0;
  const yearPercentageChange = previousYearTotal !== 0
    ? ((yearTotal - previousYearTotal) / previousYearTotal) * 100
    : 0;

  const handleAddShiftRevenue = () => {
    const now = new Date();
    const shiftDate = now.getHours() < 7 
      ? subHours(now, now.getHours() + 17)
      : now;
    
    setSelectedDate(format(shiftDate, 'yyyy-MM-dd'));
    setShowRevenueForm(true);
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    setYearDropdownOpen(false);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="space-y-4">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-8 w-40" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          ))}
        </div>

        {/* Weekly Goal */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="space-y-4">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>

        {/* Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <Skeleton className="h-[350px] w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-6">
      {/* Pull-to-refresh indicator */}
      {refreshing && (
        <div className="fixed top-0 left-0 right-0 flex items-center justify-center bg-blue-500 text-white text-sm py-1 z-50">
          Vernieuwen...
        </div>
      )}
      
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Totaaloverzicht</h1>
        
        <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-3">
          {/* Fooi Toggle Button */}
          {isCurrentYear && (
            <button
              onClick={async () => {
                const newShowTips = !showTips;
                setShowTips(newShowTips);

                try {
                  const { data: { user } } = await supabase.auth.getUser();
                  if (!user) return;

                  await supabase
                    .from('settings')
                    .update({ show_tips: newShowTips })
                    .eq('user_id', user.id);

                  await fetchData();
                } catch (error) {
                  console.error('Error updating show_tips setting:', error);
                }
              }}
              className={`w-full lg:w-auto flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-medium transition-colors ${
                showTips
                  ? 'bg-green-500/20 dark:bg-green-500/30 text-green-700 dark:text-green-300'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Coins className="h-4 w-4 mr-2" />
              <span>Fooi {showTips ? 'verbergen' : 'tonen'}</span>
            </button>
          )}

          {/* Year Selector */}
          <div className="relative w-full lg:w-auto">
            <button
              onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
              className="w-full lg:w-auto flex items-center justify-between lg:justify-start space-x-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span>Jaar: {selectedYear}</span>
              <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </button>
            
            {yearDropdownOpen && (
              <div className="absolute right-0 mt-2 w-full lg:w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                <div className="py-1">
                  {activeYears.filter(year => year >= 2023).map(year => (
                    <button
                      key={year}
                      onClick={() => handleYearChange(year)}
                      className={`block w-full text-left px-4 py-2 text-sm ${
                        year === selectedYear
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Add Shift Button */}
          <button
            onClick={handleAddShiftRevenue}
            {...tooltip('add-shift', 'Voeg omzet toe voor je laatste dienst')}
            className="hidden lg:flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <span>Dienst Afronden</span>
          </button>
        </div>
      </div>

      <div className="flex-1 grid grid-rows-[auto_auto_1fr] gap-6">
        <div className={`grid grid-cols-1 ${isCurrentYear ? 'md:grid-cols-4' : 'md:grid-cols-2'} gap-6`}>
          {isCurrentYear && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <p className="text-sm text-gray-500 dark:text-gray-400" {...tooltip('month-revenue', 'Totale omzet voor de huidige maand inclusief fooi')}>
                      Deze maand
                    </p>
                  </div>
                  <p className="mt-3 text-xl font-bold text-gray-900 dark:text-white">
                    €{totalMonthRevenue.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {format(currentDate, 'MMMM', { locale: nl }).charAt(0).toUpperCase() + format(currentDate, 'MMMM', { locale: nl }).slice(1)}
                  </p>
                  {showTips && currentMonthHiddenTotal > 0 && (
                    <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                      (incl. €{currentMonthHiddenTotal.toFixed(2)} fooi)
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isCurrentYear && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <p className="text-sm text-gray-500 dark:text-gray-400" {...tooltip('quarter-revenue', 'Totale omzet voor het huidige kwartaal inclusief fooi')}>
                      Dit kwartaal
                    </p>
                  </div>
                  <p className="mt-3 text-xl font-bold text-gray-900 dark:text-white">
                    €{totalQuarterRevenue.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Kwartaal {getQuarter(new Date())}
                  </p>
                  {showTips && quarterHiddenTotal > 0 && (
                    <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                      (incl. €{quarterHiddenTotal.toFixed(2)} fooi)
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  <p className="text-sm text-gray-500 dark:text-gray-400" {...tooltip('year-revenue', 'Totale omzet voor het huidige jaar inclusief fooi')}>
                    Omzet {selectedYear}
                  </p>
                </div>
                <p className="mt-3 text-xl font-bold text-gray-900 dark:text-white">
                  €{totalYearRevenue.toFixed(2)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {isCurrentYear ? 'Tot nu toe' : 'Totaal'}
                </p>
                {showTips && yearHiddenTotal > 0 && (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                    (incl. €{yearHiddenTotal.toFixed(2)} fooi)
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  <p className="text-sm text-gray-500 dark:text-gray-400" {...tooltip('weekly-average', 'Gemiddelde omzet per week inclusief fooi')}>
                    Gemiddeld per week
                  </p>
                </div>
                <p className="mt-3 text-xl font-bold text-gray-900 dark:text-white">
                  €{totalWeeklyAverage.toFixed(2)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {isCurrentYear ? 'Dit jaar' : selectedYear}
                </p>
              </div>
            </div>
          </div>
        </div>

        {isCurrentYear && showRevenueGoal && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Trophy className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Weekdoel
                </h2>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Week {currentWeekNumber}
              </div>
            </div>
            <div className="w-full h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden relative">
              {showTips && currentWeekHiddenTotal > 0 ? (
                <>
                  {/* Base revenue bar (blue or green) */}
                  <div
                    className="h-full transition-all duration-500 absolute top-0 left-0"
                    style={{
                      width: `${Math.min((currentWeekTotal / weeklyGoal) * 100, 100)}%`,
                      background: weekProgress >= 100
                        ? 'linear-gradient(90deg, #10B981, #059669)'
                        : 'linear-gradient(90deg, #6366F1, #4F46E5)',
                      borderRadius: '9999px 0 0 9999px'
                    }}
                  />
                  {/* Hidden revenue bar (red) */}
                  <div
                    className="h-full transition-all duration-500 absolute top-0"
                    style={{
                      left: `${Math.min((currentWeekTotal / weeklyGoal) * 100, 100)}%`,
                      width: `${Math.min((currentWeekHiddenTotal / weeklyGoal) * 100, 100 - (currentWeekTotal / weeklyGoal) * 100)}%`,
                      background: 'linear-gradient(90deg, #EF4444, #DC2626)',
                      borderRadius: weekProgress >= 100 ? '0 9999px 9999px 0' : '0'
                    }}
                  />
                </>
              ) : (
                <div
                  className="h-full transition-all duration-500 absolute top-0 left-0"
                  style={{
                    width: `${Math.min(weekProgress, 100)}%`,
                    background: weekProgress >= 100
                      ? 'linear-gradient(90deg, #10B981, #059669)'
                      : 'linear-gradient(90deg, #6366F1, #4F46E5)',
                    borderRadius: weekProgress >= 100 ? '9999px' : '9999px 0 0 9999px'
                  }}
                />
              )}
            </div>
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">
                Deze week: €{totalWeekRevenue.toFixed(2)}
                {showTips && currentWeekHiddenTotal > 0 && (
                  <span className="text-xs ml-1 text-red-500 dark:text-red-400">(incl. €{currentWeekHiddenTotal.toFixed(2)} fooi)</span>
                )}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                {weekProgress >= 100 ? (
                  "Gefeliciteerd! Doel behaald 🎉"
                ) : (
                  `Nog €${(weeklyGoal - totalWeekRevenue).toFixed(2)} te gaan`
                )}
              </span>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Omzet per week
          </h2>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={weeklyData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="#E5E7EB" 
                  strokeOpacity={0.3} 
                />
                <XAxis
                  dataKey="name"
                  stroke="#888888"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="#888888"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `€${value}`}
                />
                <Tooltip
                  formatter={(value: any, name: string) => [`${name}: €${Number(value).toFixed(2)}`, '']}
                  labelFormatter={(label) => `Week ${label}`}
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '0.5rem',
                  }}
                  itemStyle={{
                    color: '#E5E7EB',
                  }}
                  labelStyle={{
                    color: '#E5E7EB',
                    marginBottom: '0.5rem',
                  }}
                />
                <Legend />
                {chartAnimated && (
                  <>
                    <Line
                      type="monotone"
                      dataKey="totaal"
                      name="Totaal"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="contant"
                      name="Contant"
                      stroke="#22c55e"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="pin"
                      name="Pin"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="rekening"
                      name="Rekening"
                      stroke="#ec4899"
                      strokeWidth={2}
                      dot={false}
                    />
                    {showTips && (
                      <Line
                        type="monotone"
                        dataKey="fooi"
                        name="Fooi"
                        stroke="#ef4444"
                        strokeWidth={2}
                        dot={false}
                      />
                    )}
                  </>
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {showRevenueForm && selectedDate && (
        <RevenueForm
          date={selectedDate}
          onClose={() => {
            setShowRevenueForm(false);
            setSelectedDate(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
}